import { Directive, ElementRef, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
    selector: '[hamSearchDropDown]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: HamSearchDropDownDirective,
            multi: true,
        },
    ],
})
export class HamSearchDropDownDirective implements OnInit, OnDestroy, ControlValueAccessor {
    @Input() label: string = ''; // Default label text
    @Input() options: any[] = []; // Input options for autocomplete, now accepting any type
    @Input() optionKeys: string[] = []; // Keys of the object to display and search by
    @Input() returnKey: string = ''; // Key of the object to return to the form control
    @Input() multiple: boolean = false; // New input to enable multi-select mode
    @Input() hideDisplaySelection: boolean = false; // New input to return the selected object instead of the key
    @Input() showRequired: boolean = false; // add indication for required field
    @Input() returnObject: boolean = false; // New input to return the selected object instead of the key
    private _disabled: boolean = false;
    @Input() set disabled(value: boolean) {
        this._disabled = value;
        this.setDisabledState(value);
    }
    private selectedOptions: any[] = []; // Store selected options for multi-select mode
    private filteredOptions: any[] = []; // Store selected options for multi-select mode
    private wrapperElement: HTMLElement;
    private labelElement: HTMLElement;
    private inputWrapperElement: HTMLElement;
    private cancelIconWrapper: HTMLElement;
    private searchIconWrapper: HTMLElement;
    private dropdownElement: HTMLElement;
    private activeIndex: number = -1; // To keep track of the active option
    private isOptionSelected: boolean = false; // State to track if an option was selected
    private isUserInput: boolean = true;
    private isDropdownVisible: boolean = false;
    private selectedItemsElement: HTMLElement;

    private listeners: (() => void)[] = [];
    private onChange: (value: any) => void = () => {};
    private onTouched: () => void = () => {};

    constructor(private el: ElementRef, private renderer: Renderer2) {}

    ngOnInit() {
        this.createWrapper();
        this.createLabel();
        this.createInputWrapper();
        this.createSearchIcon();
        if (!this._disabled) {
            this.createCancelIcon();
        }
        this.createDropdown();

        // Setup listeners and store the references
        this.listeners.push(
            this.renderer.listen(this.el.nativeElement, 'input', () => {
                if (this.isUserInput) {
                    this.onInput();
                }
                this.isUserInput = true; // Reset flag after input event
            }),
        );
        // Setup listeners and store the references
        this.listeners.push(
            this.renderer.listen(this.el.nativeElement, 'click', () => {
                this.isDropdownVisible = false; // Ensure dropdown remains open
                this.toggleDropdown();
            }),
        );
        this.listeners.push(this.renderer.listen(this.el.nativeElement, 'keydown', (event) => this.onKeyDown(event)));

        // Listen for ESC key to close dropdown
        this.listeners.push(
            this.renderer.listen('document', 'keydown', (event) => {
                if (event.key === 'Escape' && this.isDropdownVisible) {
                    this.closeDropdown();
                }
            }),
        );

        // Listen for clicks outside the dropdown to close it
        this.listeners.push(
            this.renderer.listen('document', 'click', (event) => {
                if (this.isDropdownVisible && !this.wrapperElement.contains(event.target as Node)) {
                    this.closeDropdown();
                }
            }),
        );

        // Initialize selected items display if there are pre-selected options
        if (this.selectedOptions.length > 0) {
            this.updateSelectedItems();
        }
    }

    ngOnDestroy() {
        // Remove all the listeners when the directive is destroyed
        this.listeners.forEach((listener) => listener());
    }

    writeValue(value: any): void {
        if (this.multiple) {
            // Handle multi-select
            this.selectedOptions = Array.isArray(value) ? value : [];
            this.updateDisplay();
            this.updateSelectedItems(); // Update selected items display
        } else {
            // Handle single select
            this.updateSingleSelectDisplay(value);
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.renderer.setProperty(this.el.nativeElement, 'disabled', isDisabled);
        this.renderer.setStyle(this.el.nativeElement, 'color', isDisabled ? 'grey' : '');
    }

    /**
     * Public method to start the search operation.
     * Can be triggered programmatically to filter options and open the dropdown.
     * @param query Optional search query to override the input value.
     */
    public startSearch(query?: string): void {
        // If a query is provided, override the input value
        if (query !== undefined) {
            this.renderer.setProperty(this.el.nativeElement, 'value', query);
        }

        // Trigger the input logic
        this.isUserInput = false; // Avoid marking this as user input
        this.onInput(); // Trigger the input processing logic
        this.isDropdownVisible = false;
        this.toggleDropdown(); // Open the dropdown
    }

    private updateSingleSelectDisplay(value: any) {
        if (value) {
            if (this.hideDisplaySelection) {
                this.renderer.setProperty(this.el.nativeElement, 'value', '');
                return;
            }
            const filter = typeof value === 'object' ? value[this.returnKey] : value;
            const matchingOption = this.options.find((option) => option[this.returnKey] === filter);
            if (matchingOption) {
                const displayText = this.optionKeys
                    .map((key) => getNestedValue(matchingOption, key))
                    .filter(Boolean)
                    .join(' - ');
                this.renderer.setProperty(this.el.nativeElement, 'value', displayText);
            } else {
                this.renderer.setProperty(this.el.nativeElement, 'value', value || '');
            }
        } else {
            this.renderer.setProperty(this.el.nativeElement, 'value', '');
        }
    }

    private createWrapper() {
        this.wrapperElement = this.renderer.createElement('div');
        this.renderer.insertBefore(this.el.nativeElement.parentNode, this.wrapperElement, this.el.nativeElement);
        this.wrapperElement.classList.add('ham-search-drop-down');
    }

    private createLabel() {
        this.labelElement = this.renderer.createElement('label');
        const text = this.renderer.createText(this.label || this.el.nativeElement.placeholder);
        this.renderer.appendChild(this.labelElement, text);
        this.renderer.appendChild(this.wrapperElement, this.labelElement);
        if (this.showRequired) {
            this.renderer.addClass(this.labelElement, 'required');
        }
    }

    private createInputWrapper() {
        this.inputWrapperElement = this.renderer.createElement('div');
        this.renderer.appendChild(this.wrapperElement, this.inputWrapperElement);
        this.renderer.appendChild(this.inputWrapperElement, this.el.nativeElement);
        this.renderer.setStyle(this.inputWrapperElement, 'position', 'relative');
        this.renderer.addClass(this.inputWrapperElement, 'input-wrapper');
    }

    private createSearchIcon() {
        this.searchIconWrapper = this.renderer.createElement('span');
        this.renderer.addClass(this.searchIconWrapper, 'ham-icon');
        this.searchIconWrapper.innerHTML = '<i class="search-icon"></i>';
        this.renderer.appendChild(this.inputWrapperElement, this.searchIconWrapper);
        this.renderer.setStyle(this.searchIconWrapper, 'display', 'block');
        this.renderer.setStyle(this.searchIconWrapper, 'position', 'absolute');
        this.renderer.setStyle(this.searchIconWrapper, 'top', '0');
        this.renderer.setStyle(this.searchIconWrapper, 'left', '16px');
        this.renderer.setStyle(this.searchIconWrapper, 'transform', 'translate(0, 12.5px)');
        this.renderer.setStyle(this.searchIconWrapper, 'font-size', '16px');

        // Add click listener to toggle dropdown visibility
        this.listeners.push(this.renderer.listen(this.searchIconWrapper, 'click', () => this.toggleDropdown()));
    }

    private createCancelIcon() {
        this.cancelIconWrapper = this.renderer.createElement('span');
        this.renderer.addClass(this.cancelIconWrapper, 'ham-icon');
        this.cancelIconWrapper.innerHTML = '<i class="x-icon"></i>';
        this.listeners.push(this.renderer.listen(this.cancelIconWrapper, 'click', () => this.onCancel()));

        this.renderer.appendChild(this.inputWrapperElement, this.cancelIconWrapper);
        this.renderer.setStyle(this.cancelIconWrapper, 'display', 'none');
        this.renderer.setStyle(this.cancelIconWrapper, 'position', 'absolute');
        this.renderer.setStyle(this.cancelIconWrapper, 'top', '0');
        this.renderer.setStyle(this.cancelIconWrapper, 'right', '16px');
        this.renderer.setStyle(this.cancelIconWrapper, 'transform', 'translate(0, 15.5px)');
        this.renderer.setStyle(this.cancelIconWrapper, 'font-size', '10px');
    }

    private createDropdown() {
        this.dropdownElement = this.renderer.createElement('ul');
        this.renderer.addClass(this.dropdownElement, 'autocomplete-dropdown');
        this.renderer.setStyle(this.dropdownElement, 'position', 'absolute');
        this.renderer.setStyle(this.dropdownElement, 'top', '100%');
        this.renderer.setStyle(this.dropdownElement, 'left', '0');
        this.renderer.setStyle(this.dropdownElement, 'right', '0');
        this.renderer.setStyle(this.dropdownElement, 'border', '1px solid #ccc');
        this.renderer.setStyle(this.dropdownElement, 'background-color', '#fff');
        this.renderer.setStyle(this.dropdownElement, 'z-index', '1000');
        this.renderer.setStyle(this.dropdownElement, 'display', 'none');

        // Prevent event propagation when clicking inside the dropdown
        this.listeners.push(
            this.renderer.listen(this.dropdownElement, 'click', (event) => {
                event.stopPropagation();
            }),
        );

        this.renderer.appendChild(this.inputWrapperElement, this.dropdownElement);

        // Create selected items list
        const selectedItemsElement = this.renderer.createElement('ul');
        this.renderer.addClass(selectedItemsElement, 'selectedItems');
        this.renderer.setStyle(selectedItemsElement, 'display', 'none'); // Initially hidden
        this.renderer.appendChild(this.inputWrapperElement, selectedItemsElement);

        this.selectedItemsElement = selectedItemsElement; // Store a reference to the selected items element
    }

    private onInput() {
        if (this.isOptionSelected && !this.multiple) {
            this.isOptionSelected = false; // Reset state after an option has been selected
            return; // Skip input processing if an option was just selected
        }

        const value = this.el.nativeElement.value.toLowerCase();

        const filteredOptions = this.options.filter((option) =>
            this.optionKeys.some((key) => getNestedValue(option, key)?.toLowerCase().includes(value)),
        );
        this.filteredOptions = filteredOptions;
        this.updateDropdown(filteredOptions);
        this.activeIndex = -1; // Reset the active index

        if (value) {
            this.renderer.setStyle(this.cancelIconWrapper, 'display', 'block');
            this.isDropdownVisible = true;
            this.renderer.setStyle(this.dropdownElement, 'display', 'block');
        } else {
            this.renderer.setStyle(this.cancelIconWrapper, 'display', 'none');
            this.isDropdownVisible = false;
            this.renderer.setStyle(this.dropdownElement, 'display', 'none');
        }
    }

    private updateDropdown(filteredOptions: any[]) {
        while (this.dropdownElement.firstChild) {
            this.dropdownElement.removeChild(this.dropdownElement.firstChild);
        }

        filteredOptions
            .filter((option) => !this.selectedOptions.includes(option)) // Exclude already selected options
            .forEach((option, index) => {
                const optionElement = this.renderer.createElement('li');
                const displayText = this.optionKeys
                    .map((key) => getNestedValue(option, key))
                    .filter(Boolean)
                    .join(' - ');
                const optionText = this.renderer.createText(displayText);
                this.renderer.appendChild(optionElement, optionText);
                this.listeners.push(this.renderer.listen(optionElement, 'click', () => this.onSelectOption(option)));
                this.renderer.appendChild(this.dropdownElement, optionElement);

                if (index === this.activeIndex) {
                    this.renderer.addClass(optionElement, 'active');
                }
            });

        if (filteredOptions.length > 0 && !this.isOptionSelected) {
            this.renderer.setStyle(this.dropdownElement, 'display', 'block');
        } else {
            this.renderer.setStyle(this.dropdownElement, 'display', 'none');
        }
    }

    private toggleDropdown() {
        this.isDropdownVisible = !this.isDropdownVisible; // Toggle visibility flag only in single-select mode

        if (this.isDropdownVisible) {
            const value = this.el.nativeElement.value.toLowerCase();
            const filteredOptions = this.options.filter((option) =>
                this.optionKeys.some((key) => getNestedValue(option, key)?.toLowerCase().includes(value)),
            );
            this.filteredOptions = filteredOptions;
            if (filteredOptions.length === 0) {
                this.isDropdownVisible = false;
                this.renderer.setStyle(this.dropdownElement, 'display', 'none');
                return;
            }
            this.updateDropdown(filteredOptions);
            this.renderer.setStyle(this.dropdownElement, 'display', 'block');
        } else {
            this.renderer.setStyle(this.dropdownElement, 'display', 'none');
        }
    }

    private closeDropdown() {
        this.isDropdownVisible = false;
        this.renderer.setStyle(this.dropdownElement, 'display', 'none');
        this.activeIndex = -1; // Reset active index
    }

    private onKeyDown(event: KeyboardEvent) {
        const options = this.dropdownElement.children;

        if (event.key === 'ArrowDown') {
            this.activeIndex = (this.activeIndex + 1) % options.length;
            this.highlightOption();
        } else if (event.key === 'ArrowUp') {
            this.activeIndex = (this.activeIndex - 1 + options.length) % options.length;
            this.highlightOption();
        } else if (event.key === 'Enter' && this.activeIndex >= 0) {
            const activeOption = options[this.activeIndex] as HTMLElement;
            if (activeOption) {
                this.onSelectOption(this.filteredOptions[this.activeIndex]);
            }
            event.preventDefault(); // Prevent form submission
        }
    }

    private highlightOption() {
        const options = this.dropdownElement.children;
        for (let i = 0; i < options.length; i++) {
            const optionElement = options[i] as HTMLElement;
            if (i === this.activeIndex) {
                this.renderer.addClass(optionElement, 'active');
            } else {
                this.renderer.removeClass(optionElement, 'active');
            }
        }
    }

    private onSelectOption(option: any) {
        this.isUserInput = false;
        if (this.multiple) {
            const index = this.selectedOptions.indexOf(option);
            if (index > -1) {
                this.selectedOptions.splice(index, 1); // Remove if already selected
            } else {
                this.selectedOptions.push(option); // Add if not selected
            }
            this.updateDisplay();
            this.updateSelectedItems(); // Update the selected items display

            // Filter options based on the current input value
            const inputValue = this.el.nativeElement.value.toLowerCase();
            const filteredOptions = this.options.filter((option) =>
                this.optionKeys.some((key) => getNestedValue(option, key)?.toLowerCase().includes(inputValue)),
            );
            this.filteredOptions = filteredOptions;
            this.updateDropdown(filteredOptions); // Update dropdown with filtered and unselected items

            // Keep the dropdown open for multi-select mode
            this.isDropdownVisible = true;
            this.renderer.setStyle(this.dropdownElement, 'display', 'block');
        } else {
            const displayText = this.optionKeys
                .map((key) => getNestedValue(option, key))
                .filter(Boolean)
                .join(' - ');

            const returnValue = this.returnObject ? option : option[this.returnKey];
            this.renderer.setProperty(this.el.nativeElement, 'value', displayText);
            this.isDropdownVisible = false;
            this.renderer.setStyle(this.dropdownElement, 'display', 'none');
            this.onChange(returnValue);
            this.activeIndex = -1;
            this.isOptionSelected = true;
            this.onTouched();
        }
    }

    private updateDisplay() {
        if (!this.multiple) {
            const displayText = this.selectedOptions
                .map((option) =>
                    this.optionKeys
                        .map((key) => getNestedValue(option, key))
                        .filter(Boolean)
                        .join(' - '),
                )
                .join(', ');
            this.renderer.setProperty(this.el.nativeElement, 'value', displayText);
        }

        const returnValue = this.selectedOptions.map((option) => (this.returnObject ? option : option[this.returnKey]));
        this.onChange(returnValue);
    }

    private updateSelectedItems() {
        // Clear the current selected items
        while (this.selectedItemsElement.firstChild) {
            this.selectedItemsElement.removeChild(this.selectedItemsElement.firstChild);
        }

        if (this.selectedOptions.length > 0) {
            this.renderer.setStyle(this.selectedItemsElement, 'display', 'block'); // Show the selected items list

            // Add each selected item to the list
            this.selectedOptions.forEach((option) => {
                const itemElement = this.renderer.createElement('li');
                this.renderer.addClass(itemElement, 'selectedItem');

                const itemContent = this.renderer.createElement('span');
                this.renderer.addClass(itemContent, 'icon-wrapper');

                const displayText = this.optionKeys
                    .map((key) => getNestedValue(option, key))
                    .filter(Boolean)
                    .join(' - ');
                const textContent = this.renderer.createElement('span');
                const textNode = this.renderer.createText(displayText);

                const removeIcon = this.renderer.createElement('i');
                this.renderer.addClass(removeIcon, 'x-icon');

                this.renderer.appendChild(itemContent, removeIcon);
                this.renderer.appendChild(textContent, textNode);
                this.renderer.appendChild(itemElement, textContent);
                this.renderer.appendChild(itemElement, itemContent);

                // Add click listener to remove item
                this.listeners.push(
                    this.renderer.listen(removeIcon, 'click', () => {
                        this.selectedOptions.splice(this.selectedOptions.indexOf(option), 1); // Remove the option
                        this.updateDisplay();
                        this.updateSelectedItems(); // Update the selected items display
                    }),
                );

                this.renderer.appendChild(this.selectedItemsElement, itemElement);
            });
        } else {
            this.renderer.setStyle(this.selectedItemsElement, 'display', 'none'); // Hide if no selected items
        }
    }

    private onCancel() {
        this.renderer.setProperty(this.el.nativeElement, 'value', ''); // Clear the input text
        this.isDropdownVisible = false;
        this.renderer.setStyle(this.dropdownElement, 'display', 'none');

        if (this.multiple) {
            // Do not clear the selected options; only update the display
            this.updateSelectedItems(); // Ensure the selected items are still shown
        } else {
            this.selectedOptions = [];
            this.onChange('');
        }
    }
}
const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((acc, key) => acc && acc[key], obj);
};
